import React from 'react';
import { Reveal } from '../components/Reveal';
import { Cpu, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackLandingCTA } from '@/lib/analytics';

/**
 * The two R43 cards the marketing ruling M1 (2026-10-20) keeps. The
 * discovery-era cards ("Production-Ready Confidence", "Paper-first, gated
 * go-live") were deleted (M1 delete list, items 11-13); the go-live promise
 * is now step 4 of HowItWorks, marked not open. The second card's old
 * "ledger you can review" became "is recorded": there is no single screen
 * today that shows proposals, approvals and orders together (M1 [verify]).
 *
 * Implementation plan P0-09 (2026-10-22): "pre-registered" is gone (the
 * platform has no pre-registration feature), and the second card says when
 * the drawdown limits are actually checked: on each rebalance, not around the
 * clock (PLATFORM_ENFORCEMENT_AUDIT section B; continuous checks are P1-01).
 */
const differentiators = [
  {
    icon: Sparkles,
    title: 'The agent proposes, inside your mandate',
    description:
      'You sign your risk limits (we call this document a mandate): the assets, limits and risk you accept. The agent reviews a small library of documented, published strategies and proposes plans that fit inside it. You approve or reject every proposal. The agent never places an order and cannot change your limits.',
    gradient: 'from-[#2dd4a0]/10 to-[#2dd4a0]/[0.02]',
    border: 'border-[#2dd4a0]/20',
    iconColor: 'text-[#2dd4a0]',
  },
  {
    icon: Cpu,
    title: 'Code enforces the limits, every action on record',
    description:
      'Your limits are enforced by deterministic code outside the AI: every order is checked against them before it is sent, and the drawdown limits are checked each time the plan rebalances. Checking them around the clock is still being built. The agent cannot raise them, resume after a halt, change credentials or withdraw. Every proposal, approval and order is recorded.',
    gradient: 'from-amber-500/10 to-amber-500/[0.02]',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-400',
  },
];

export const Features: React.FC<{ onSignUp?: (tier?: string) => void }> = ({ onSignUp }) => {
  return (
    <section id="features" className="py-28 px-6">
      <div className="max-w-[1000px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm text-[#2dd4a0] font-medium uppercase tracking-widest mb-4">
            Why Mendl Labs
          </p>
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            An AI that can propose.{' '}
            <span className="text-gradient">It cannot overrule your limits.</span>
          </h2>
        </Reveal>

        <Reveal
          className="grid md:grid-cols-2 gap-6"
        >
          {differentiators.map((item, i) => (
            <Reveal
              key={item.title}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className={`relative group p-8 rounded-2xl bg-gradient-to-br ${item.gradient} border ${item.border} hover:border-opacity-50 transition-all duration-300`}
            >
              <div className={`w-12 h-12 rounded-xl bg-white/[0.04] flex items-center justify-center mb-6 ${item.iconColor}`}>
                <item.icon size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                {item.title}
              </h3>
              <p className="text-white/50 leading-relaxed">
                {item.description}
              </p>
            </Reveal>
          ))}
        </Reveal>

        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Button size="lg" onClick={() => { trackLandingCTA('features'); onSignUp?.('observer'); }} className="px-8 py-6 text-base glow-primary">
            Open a paper account
            <span className="ml-2">&rarr;</span>
          </Button>
        </Reveal>
      </div>
    </section>
  );
};
